import { MenuItemType } from "../../../../types/baseTypes";
import { customSanitize } from "../../../../utils/customSanitize";
import SubMenu from "../SubMenu/SubMenu";
import styles from "./MenItem.module.scss";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { MenuParentType } from "../Menu";
import {
  isChildCurrentPage,
  isMenuItemCurrentPage,
  isMenuItemOrChildCurrentPage,
  isMenuOrChildMenuSelected,
} from "../utils";
import { useAppContext } from "../../../../context/AppContext";
import { RefObject, useEffect } from "react";
import { useLenis } from "lenis/react";
import { optimizeURL } from "@/utils/url";
import { Post } from "@aleksundshantu/aunds-hofreiter2026-types/client";
import { CustomLink } from "@/components/CustomLink";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client";
import { usePathname } from "next/navigation";

const HIGHTLIGHT_CURRENT_PAGE = true;
const HIGHLIGHT_CURRENT_PAGES_PARENTS = true;

interface MenuItemProps {
  item: MenuItemType;
  pageData?: Post;
  currentSubmenu: MenuParentType;
  setCurrentSubmenu: (value: MenuParentType) => void;
  parentSubMenu: MenuParentType;
  tabIndex: number;
  itemRef: RefObject<HTMLLIElement>;
  controlParentMenu: (action: "close" | "prev" | "next") => void;
  onFocus?: () => void;
  subMenu: MenuItemType;
}

const MenuItem = ({
  item,
  pageData,
  currentSubmenu,
  setCurrentSubmenu,
  parentSubMenu,
  tabIndex,
  itemRef,
  controlParentMenu,
  onFocus,
  subMenu,
}: MenuItemProps) => {
  const { setOpenMenu } = useAppContext();
  const { label } = useGlobalContext();
  const lenis = useLenis();
  const hasChildren = item.child_items && item.child_items.length > 0;
  const isSimpleLanguage = item.classes?.includes("simple-language");

  const pathname = usePathname();

  const isCurrentPage = HIGHTLIGHT_CURRENT_PAGE
    ? HIGHLIGHT_CURRENT_PAGES_PARENTS
      ? isMenuItemOrChildCurrentPage(item, pageData)
      : isMenuItemCurrentPage(item, pageData)
    : false;

  const { getIcon } = useGlobalContext();

  const isInSubMenu = parentSubMenu !== undefined;

  const mouseOverOrFocus = () => {
    if (!isMenuOrChildMenuSelected(item, currentSubmenu) && !parentSubMenu) {
      setCurrentSubmenu(undefined);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (itemRef.current && !itemRef.current.contains(event.target as Node)) {
        setCurrentSubmenu(undefined);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCurrentSubmenu(undefined);
      }
    };

    if (currentSubmenu?.ID === item.ID) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [currentSubmenu, item.ID, setCurrentSubmenu, itemRef]);

  return (
    <li
      role="none"
      className={`${styles["menu-item"]} menu-item ${isCurrentPage ? "is-active" : ""}`}
      ref={itemRef}
    >
      {hasChildren && (
        <>
          <button
            className={`${isInSubMenu ? "navbar-sublink" : "navbar-link"} ${isCurrentPage ? "is-active" : ""} is-flex is-align-items-center is-gap-2 button-reset`}
            onClick={(e) => {
              if (e.screenX === 0 && e.screenY === 0) {
                // https://stackoverflow.com/a/61323763
                // enter pressed - handled by menu onKeyDown
                return;
              }
              if (currentSubmenu?.ID === item.ID) {
                setCurrentSubmenu(parentSubMenu);
                return;
              }
              setCurrentSubmenu(item);
            }}
            onMouseOver={mouseOverOrFocus}
            onFocus={() => {
              mouseOverOrFocus();
              onFocus?.();
            }}
            aria-expanded={isMenuOrChildMenuSelected(item, currentSubmenu)}
            role="menuitem"
            aria-haspopup={"true"}
            title={
              isChildCurrentPage(item, pageData)
                ? label("global", null, "nav_item_includes_current_page", {
                    en: "Includes current page",
                    de: "Beinhaltet aktuelle Seite",
                  })
                : ""
            }
            tabIndex={tabIndex}
          >
            <span
              className={`${styles["menu-item-link"]}`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(item.title),
              }}
            />
            <CustomSVG
              width={16}
              height={16}
              className="is-hidden-after-navbar-breakpoint navbar-sublink"
              src={getIcon("arrowRight")}
              wrapper={"span"}
              aria-hidden="true"
            />
          </button>

          <SubMenu
            item={item}
            pageData={pageData}
            currentSubmenu={currentSubmenu}
            setCurrentSubmenu={setCurrentSubmenu}
            parentSubMenu={parentSubMenu}
            subMenu={subMenu}
            parentMenuItem={item}
            controlParentMenu={controlParentMenu}
          />
        </>
      )}

      {!hasChildren && (
        <CustomLink
          className={`${isInSubMenu ? "navbar-sublink" : "navbar-link"} ${styles["menu-item-link"]} ${isCurrentPage ? "is-active" : ""} ${isSimpleLanguage ? styles["has-simple-language-icon"] : ""}`}
          href={item.url}
          target={"_self"}
          aria-label={item.title}
          role="menuitem"
          onClick={() => {
            // if nav item has anchor (#) which is on the current page use lenis to scroll to anchor for animation and otherwise it won't work if lenis is currently animating page scroll
            // https://aleksundshantu.slack.com/archives/C08166QC5B2/p1734104421062139
            const url = optimizeURL(item.url);
            if (url && lenis) {
              const urlAnchor = url.split("#")[1];
              const urlWithoutAnchor = url.split("?")[0];
              const element = document.getElementById(urlAnchor);
              const nav = document.querySelector("nav.navbar") as
                HTMLElement | undefined;
              if (pathname === urlWithoutAnchor && element) {
                lenis.scrollTo(element.offsetTop - (nav?.offsetHeight ?? 0));
              }
            }

            setOpenMenu(false);
            setOpenMenu(false);
            setCurrentSubmenu(undefined);
          }}
          {...(isCurrentPage && { "aria-current": "page" })}
          tabIndex={tabIndex}
          onFocus={onFocus}
          disablePrefetch // disable prefetch for menu items https://trello.com/c/g5k075FS
        >
          {item.title}
          {isSimpleLanguage && (
            <CustomSVG
              width={20}
              height={20}
              className={styles["menu-item-icon"]}
              src={getIcon("simpleLanguage")}
              wrapper={"span"}
              aria-hidden="true"
            />
          )}
        </CustomLink>
      )}
    </li>
  );
};

export default MenuItem;
